// ============================================================
// AnnotationLayer
// ============================================================
// • Click anywhere in annotation mode → places annotation, exits mode
// • Click an existing annotation → selects it, shows style panel
//   (text input, bold, italic, color, font size, delete)
// • Drag an existing annotation → moves it
// • Double-click → edit text inline (textarea)
// • Uses annotation.xRatio [0..1] and annotation.yOffset [px]
// ============================================================

import React, {
  useRef, useState, useCallback, useEffect,
} from 'react';
import type { Annotation, AnnotationStyle } from '../../core/types';
import { useSongStore } from '../../store/songStore';

interface AnnotationLayerProps {
  chartRef: React.RefObject<HTMLDivElement>;
  annotationMode: boolean;
  onExitAnnotationMode?: () => void;
}

interface DragState {
  id: string;
  startMouseX: number;
  startMouseY: number;
  startXRatio: number;
  startYOffset: number;
}

const COLORS = ['#e63946', '#2196f3', '#4caf50', '#ff9800', '#9c27b0', '#333333'];

export function AnnotationLayer({
  chartRef,
  annotationMode,
  onExitAnnotationMode,
}: AnnotationLayerProps) {
  const annotations              = useSongStore((s) => s.song.annotations ?? []);
  const addAnnotation            = useSongStore((s) => s.addAnnotation);
  const updateAnnotationPosition = useSongStore((s) => s.updateAnnotationPosition);
  const updateAnnotationText     = useSongStore((s) => s.updateAnnotationText);
  const updateAnnotationStyle    = useSongStore((s) => s.updateAnnotationStyle);
  const deleteAnnotation         = useSongStore((s) => s.deleteAnnotation);

  const layerRef                    = useRef<HTMLDivElement>(null);
  const [drag, setDrag]             = useState<DragState | null>(null);
  const [editingId, setEditingId]   = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Deselect when clicking outside
  useEffect(() => {
    function onDown(e: MouseEvent) {
      const layer = layerRef.current;
      if (!layer) return;
      if (!(e.target as HTMLElement).closest('[data-annotation]') &&
          !(e.target as HTMLElement).closest('[data-annotation-panel]')) {
        setSelectedId(null);
        setEditingId(null);
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  // ── Place annotation ──────────────────────────────────────

  function handleLayerClick(e: React.MouseEvent<HTMLDivElement>) {
    if (!annotationMode) return;
    if ((e.target as HTMLElement).closest('[data-annotation]')) return;

    const rect = layerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const xRatio  = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const yOffset = Math.max(0, e.clientY - rect.top);

    const newId = addAnnotation(xRatio, yOffset);
    // Select the new annotation immediately so user sees the panel
    setSelectedId(newId);
    setEditingId(newId);
    onExitAnnotationMode?.();
  }

  // ── Drag ─────────────────────────────────────────────────

  const handleAnnotationMouseDown = useCallback(
    (e: React.MouseEvent, annotation: Annotation) => {
      if (annotationMode) return;
      if ((e.target as HTMLElement).closest('[data-annotation-control]')) return;
      if ((e.target as HTMLElement).closest('textarea')) return;
      e.stopPropagation();

      // Single click = select
      setSelectedId(annotation.id);

      // Start drag
      setDrag({
        id:           annotation.id,
        startMouseX:  e.clientX,
        startMouseY:  e.clientY,
        startXRatio:  annotation.xRatio,
        startYOffset: annotation.yOffset,
      });
    },
    [annotationMode],
  );

  useEffect(() => {
    if (!drag) return;
    let moved = false;

    function onMouseMove(e: MouseEvent) {
      if (!drag || !layerRef.current) return;
      moved = true;
      const rect = layerRef.current.getBoundingClientRect();
      const dx = (e.clientX - drag.startMouseX) / rect.width;
      const dy =  e.clientY - drag.startMouseY;
      updateAnnotationPosition(
        drag.id,
        Math.max(0, Math.min(1, drag.startXRatio  + dx)),
        Math.max(0,             drag.startYOffset + dy),
      );
    }

    function onMouseUp() { setDrag(null); }

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup',   onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup',   onMouseUp);
    };
  }, [drag, updateAnnotationPosition]);

  // ── Render ────────────────────────────────────────────────

  return (
    <div
      ref={layerRef}
      className="absolute inset-0 z-20 pointer-events-none"
    >
      {/* Click-capture overlay in annotation mode */}
      {annotationMode && (
        <div
          className="absolute inset-0 pointer-events-auto"
          style={{ cursor: 'crosshair' }}
          onClick={handleLayerClick}
        />
      )}

      {annotations.map((ann) => (
        <AnnotationPin
          key={ann.id}
          annotation={ann}
          isEditing={editingId === ann.id}
          isSelected={selectedId === ann.id}
          isDragging={drag?.id === ann.id}
          annotationMode={annotationMode}
          onMouseDown={(e) => handleAnnotationMouseDown(e, ann)}
          onDoubleClick={() => { setSelectedId(ann.id); setEditingId(ann.id); }}
          onBlur={(text) => { updateAnnotationText(ann.id, text); setEditingId(null); }}
          onDelete={() => { deleteAnnotation(ann.id); setSelectedId(null); }}
          onStyleChange={(style) => updateAnnotationStyle(ann.id, style)}
        />
      ))}
    </div>
  );
}

// ── AnnotationPin ─────────────────────────────────────────────

interface AnnotationPinProps {
  annotation:    Annotation;
  isEditing:     boolean;
  isSelected:    boolean;
  isDragging:    boolean;
  annotationMode: boolean;
  onMouseDown:   (e: React.MouseEvent) => void;
  onDoubleClick: () => void;
  onBlur:        (text: string) => void;
  onDelete:      () => void;
  onStyleChange: (style: Partial<AnnotationStyle>) => void;
}

function AnnotationPin({
  annotation, isEditing, isSelected, isDragging, annotationMode,
  onMouseDown, onDoubleClick, onBlur, onDelete, onStyleChange,
}: AnnotationPinProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const draftRef    = useRef(annotation.text);

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, [isEditing]);

  const style = annotation.style;

  return (
    <div
      data-annotation
      className="absolute pointer-events-auto"
      style={{
        left:      `${annotation.xRatio * 100}%`,
        top:       `${annotation.yOffset}px`,
        transform: 'translate(-50%, -50%)',
        zIndex:    isDragging ? 60 : isSelected ? 50 : 30,
        cursor:    annotationMode ? 'crosshair' : (isDragging ? 'grabbing' : 'grab'),
        userSelect: 'none',
      }}
      onMouseDown={onMouseDown}
      onDoubleClick={onDoubleClick}
    >
      {/* Text body */}
      {isEditing ? (
        <textarea
          ref={textareaRef}
          defaultValue={annotation.text}
          onChange={(e) => { draftRef.current = e.currentTarget.value; }}
          style={{
            minWidth: 120, maxWidth: 280, minHeight: 36,
            padding: '5px 10px',
            background: 'var(--color-paper)',
            border: '2px solid var(--color-accent)',
            borderRadius: 6,
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
            fontSize: style?.fontSize ?? 13,
            fontStyle: style?.italic ? 'italic' : 'normal',
            fontWeight: style?.bold ? 700 : 400,
            color: style?.color ?? 'var(--color-ink-primary)',
            resize: 'both',
            outline: 'none',
            lineHeight: 1.4,
            cursor: 'text',
            userSelect: 'text',
          }}
          onBlur={() => onBlur(draftRef.current)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onBlur(draftRef.current); }
            if (e.key === 'Escape') { draftRef.current = annotation.text; onBlur(annotation.text); }
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <div
          style={{
            maxWidth: 240,
            padding: '5px 10px',
            background: 'var(--color-paper)',
            border: isSelected
              ? '2px solid var(--color-accent)'
              : '1px solid var(--color-rule)',
            borderRadius: 6,
            boxShadow: isDragging
              ? '0 8px 24px rgba(0,0,0,0.18)'
              : isSelected
              ? '0 4px 12px rgba(0,0,0,0.12)'
              : '0 2px 6px rgba(0,0,0,0.07)',
            fontSize: style?.fontSize ?? 13,
            fontStyle: style?.italic ? 'italic' : 'normal',
            fontWeight: style?.bold ? 700 : 400,
            color: style?.color ?? 'var(--color-ink-primary)',
            lineHeight: 1.4,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            transition: 'box-shadow 0.1s, border-color 0.1s',
          }}
        >
          {annotation.text || <span style={{ opacity: 0.35, fontStyle: 'italic' }}>Note…</span>}
        </div>
      )}

      {/* Style panel — shown when selected and not dragging */}
      {isSelected && !isDragging && (
        <div
          data-annotation-panel
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--color-paper)',
            border: '1px solid var(--color-rule)',
            borderRadius: 8,
            boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
            padding: '8px 10px',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            zIndex: 100,
            whiteSpace: 'nowrap',
            userSelect: 'none',
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* Edit text button */}
          <PanelButton
            title="Edit text"
            onClick={onDoubleClick}
            active={isEditing}
          >
            ✏️
          </PanelButton>

          <Divider />

          {/* Bold */}
          <PanelButton
            title="Bold"
            onClick={() => onStyleChange({ bold: !style?.bold })}
            active={!!style?.bold}
            style={{ fontWeight: 700, fontSize: 13 }}
          >
            B
          </PanelButton>

          {/* Italic */}
          <PanelButton
            title="Italic"
            onClick={() => onStyleChange({ italic: !style?.italic })}
            active={!!style?.italic}
            style={{ fontStyle: 'italic', fontSize: 13 }}
          >
            I
          </PanelButton>

          <Divider />

          {/* Font size */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <PanelButton title="Smaller" onClick={() => onStyleChange({ fontSize: Math.max(9, (style?.fontSize ?? 13) - 1) })}>−</PanelButton>
            <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--color-ink-muted)', minWidth: 20, textAlign: 'center' }}>
              {style?.fontSize ?? 13}
            </span>
            <PanelButton title="Larger" onClick={() => onStyleChange({ fontSize: Math.min(32, (style?.fontSize ?? 13) + 1) })}>+</PanelButton>
          </div>

          <Divider />

          {/* Color swatches */}
          {COLORS.map((c) => (
            <button
              key={c}
              title={c}
              onClick={() => onStyleChange({ color: c })}
              style={{
                width: 14, height: 14, borderRadius: '50%',
                background: c, border: 'none', cursor: 'pointer', flexShrink: 0,
                outline: style?.color === c ? '2px solid var(--color-accent)' : '2px solid transparent',
                outlineOffset: 1,
              }}
            />
          ))}

          <Divider />

          {/* Delete */}
          <PanelButton
            title="Delete annotation"
            onClick={onDelete}
            style={{ color: '#e63946' }}
          >
            🗑
          </PanelButton>
        </div>
      )}
    </div>
  );
}

// ── Small helpers ─────────────────────────────────────────────

function Divider() {
  return (
    <div style={{ width: 1, height: 16, background: 'var(--color-rule)', flexShrink: 0 }} />
  );
}

function PanelButton({
  children, onClick, title, active = false, style: extraStyle = {},
}: {
  children: React.ReactNode;
  onClick: () => void;
  title?: string;
  active?: boolean;
  style?: React.CSSProperties;
}) {
  return (
    <button
      title={title}
      onClick={onClick}
      style={{
        background: active ? 'var(--color-accent)' : 'transparent',
        color: active ? '#fff' : 'var(--color-ink-secondary)',
        border: 'none',
        borderRadius: 4,
        width: 24, height: 24,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer',
        fontSize: 12,
        flexShrink: 0,
        transition: 'background 0.1s',
        ...extraStyle,
      }}
    >
      {children}
    </button>
  );
}